import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  IoMedicalOutline, 
  IoSearch, 
  IoCloseCircle, 
  IoCalendarOutline, 
  IoFlaskOutline, 
  IoShieldCheckmarkOutline, 
  IoBarcodeOutline, 
  IoBusiness, 
  IoPersonOutline 
} from 'react-icons/io5';

// Web Storage wrapper (using localStorage)
const storage = {
  getItem: (key) => {
    try {
      return localStorage.getItem(key);
    } catch (error) {
      //console.error('Error getting item from localStorage:', error);
      return null;
    }
  },
  setItem: (key, value) => {
    try {
      localStorage.setItem(key, value);
    } catch (error) {
      //console.error('Error setting item in localStorage:', error);
    }
  }
};

// Styles
const styles = {
  mainContainer: {
    minHeight: '100vh',
    backgroundColor: '#f5f5f5',
    display: 'flex',
    flexDirection: 'column',
  },
  scrollContainer: {
    flex: 1,
    padding: '16px',
    maxWidth: '800px',
    margin: '0 auto',
    width: '100%',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '40px',
    minHeight: '400px',
  },
  loadingSpinner: {
    width: '40px',
    height: '40px',
    border: '4px solid #f3f3f3',
    borderTop: '4px solid #137a7f',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  loadingText: {
    marginTop: '16px',
    fontSize: '16px',
    color: '#666',
    textAlign: 'center',
  },
  searchContainer: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: '#fff',
    borderRadius: '12px',
    paddingHorizontal: '16px',
    marginBottom: '20px',
    borderWidth: '1px',
    borderColor: '#ddd',
    borderStyle: 'solid',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  },
  searchIcon: {
    marginRight: '12px',
    fontSize: '20px',
    color: '#999',
  },
  input: {
    flex: 1,
    height: '50px',
    fontSize: '16px',
    color: '#333',
    border: 'none',
    outline: 'none',
    backgroundColor: 'transparent',
  },
  clearButton: {
    padding: '4px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'none',
    border: 'none',
  },
  clearIcon: {
    fontSize: '20px',
    color: '#999',
  },
  resultsCount: {
    fontSize: '14px',
    color: '#666',
    marginBottom: '16px',
    textAlign: 'center',
  },
  card: {
    backgroundColor: '#fff',
    borderRadius: '16px',
    padding: '20px',
    marginBottom: '16px',
    boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
    borderWidth: '1px',
    borderColor: '#e0e0e0',
    borderStyle: 'solid',
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '16px',
    paddingBottom: '16px',
    borderBottomWidth: '1px',
    borderBottomColor: '#f0f0f0',
    borderBottomStyle: 'solid',
  },
  cardIcon: {
    fontSize: '24px',
    color: '#137a7f',
    marginRight: '12px',
  },
  vaccineName: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#137a7f',
    flex: 1,
  },
  cardContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    marginBottom: '16px',
  },
  infoRow: {
    display: 'flex',
    alignItems: 'flex-start',
  },
  infoIcon: {
    marginTop: '2px',
    marginRight: '12px',
    fontSize: '18px',
    color: '#666',
  },
  infoContent: {
    flex: 1,
  },
  label: {
    fontSize: '12px',
    fontWeight: '500',
    color: '#999',
    marginBottom: '2px',
    textTransform: 'uppercase',
  },
  value: {
    fontSize: '16px',
    color: '#333',
    lineHeight: '22px',
  },
  cardFooter: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    paddingTop: '16px',
    borderTopWidth: '1px',
    borderTopColor: '#f0f0f0',
    borderTopStyle: 'solid',
  },
  vaccineStatus: {
    fontSize: '14px',
    color: '#4caf50',
    fontWeight: '500',
    marginLeft: '8px',
  },
  emptyContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px',
  },
  emptyIcon: {
    fontSize: '64px',
    color: '#ccc',
  },
  emptyText: {
    fontSize: '16px',
    color: '#666',
    textAlign: 'center',
    marginTop: '16px',
  },
  emptySubText: {
    fontSize: '14px',
    color: '#666',
    textAlign: 'center',
    marginTop: '8px',
  },
  navbar: {
    backgroundColor: '#137a7f',
    padding: '16px 20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  },
  navbarTitle: {
    color: 'white',
    fontSize: '18px',
    fontWeight: '600',
    margin: 0,
  },
  backButton: {
    position: 'absolute',
    left: '20px',
    background: 'none',
    border: 'none',
    color: 'white',
    fontSize: '20px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  backButtonText: {
    fontSize: '14px',
  },
  footer: {
    backgroundColor: '#f5f5f5',
    padding: '10px',
    textAlign: 'center',
    borderTopWidth: '1px',
    borderTopColor: '#e0e0e0',
    borderTopStyle: 'solid',
  },
  footerText: {
    fontSize: '12px',
    color: '#666',
    margin: 0,
  },
};

// Add keyframes animation
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;
document.head.appendChild(styleSheet);

// NavBar Component
const NavBar = ({ title, showBackButton = false }) => {
  const navigate = useNavigate();

  return (
    <div style={styles.navbar}>
      {showBackButton && (
        <button
          onClick={() => navigate(-1)}
          style={styles.backButton}
        >
          ← <span style={styles.backButtonText}>Voltar</span>
        </button>
      )}
      <h1 style={styles.navbarTitle}>{title}</h1>
    </div>
  );
};

// Footer Component
const Footer = () => {
  const currentYear = new Date().getFullYear();
  return (
    <footer style={styles.footer}>
      <p style={styles.footerText}>© {currentYear} - Sistema de Saúde</p>
    </footer>
  );
};

// InfoItem Component
const InfoItem = ({ icon: IconComponent, label, value }) => {
  const safeValue = value || 'Não informado';
  
  return (
    <div style={styles.infoRow}>
      <div style={styles.infoIcon}>
        <IconComponent />
      </div>
      <div style={styles.infoContent}>
        <div style={styles.label}>{label}</div>
        <div style={styles.value}>{safeValue}</div>
      </div>
    </div>
  );
};

export default function VacinasScreen() {
  const [vacinas, setVacinas] = useState([]);
  const [filtro, setFiltro] = useState('');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const storedPesCod = storage.getItem('cpf');
        const storedLink = storage.getItem('link');
        const storedChave = storage.getItem('chave');
        const storedGuid = storage.getItem('govbr_guid');
        const guidParam = storedGuid ? `&GUID=${storedGuid}` : '';

        if (!guidParam) {
          setLoading(false);
          return;
        }

        const url = `${storedLink}/apidadospaciente.rule?sys=SAU&p1=${storedChave}&p2=vacina&p10=${storedPesCod}${guidParam}`;
        //console.log('URL da API (vacinas):', url);
        
        const response = await fetch(url);
        const data = await response.json();

        //console.log('Resposta da API (vacinas):', data);

        if (data.status === 'success' && data.dados && Array.isArray(data.dados)) {
          setVacinas(data.dados);
        } else {
          //console.log('API não retornou vacinas válidas');
          setVacinas([]);
        }
      } catch (error) {
        //console.log('Erro ao buscar vacinas:', error);
        setVacinas([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Função para verificar se há dados
  const hasAnyData = () => {
    return Array.isArray(vacinas) && vacinas.length > 0;
  };

  const formatarData = (dataString) => {
    if (!dataString) return 'Não aplicada';
    try {
      const data = new Date(dataString);
      return data.toLocaleDateString('pt-BR');
    } catch (error) {
      //console.error('Erro ao formatar data:', error);
      return dataString;
    }
  };

  const vacinasFiltradas = vacinas.filter(item =>
    item.IMUNOBIOLOGICO?.toLowerCase().includes(filtro.toLowerCase())
  );

  return (
    <div style={styles.mainContainer}>
      <NavBar title="Vacinas" showBackButton={true} />

      <div style={styles.scrollContainer}>
        {loading ? (
          <div style={styles.loadingContainer}>
            <div style={styles.loadingSpinner} />
            <div style={styles.loadingText}>Carregando vacinas...</div>
          </div>
        ) : !hasAnyData() ? (
          <div style={styles.emptyContainer}>
            <IoMedicalOutline style={styles.emptyIcon} />
            <div style={styles.emptyText}>Nenhuma vacina encontrada</div>
            <div style={styles.emptySubText}>
              Consulte a Unidade de Saúde para verificar seu histórico de vacinação.
            </div>
          </div>
        ) : (
          <>
            <div style={styles.searchContainer}>
              <IoSearch style={styles.searchIcon} />
              <input
                type="text"
                style={styles.input}
                placeholder="Pesquisar vacina..."
                value={filtro}
                onChange={(e) => setFiltro(e.target.value)}
              />
              {filtro.length > 0 && (
                <button 
                  onClick={() => setFiltro('')} 
                  style={styles.clearButton}
                >
                  <IoCloseCircle style={styles.clearIcon} />
                </button>
              )}
            </div>

            <div style={styles.resultsCount}>
              {vacinasFiltradas.length} vacina{vacinasFiltradas.length !== 1 ? 's' : ''} encontrada{vacinasFiltradas.length !== 1 ? 's' : ''}
            </div>

            {vacinasFiltradas.length > 0 ? (
              vacinasFiltradas.map((item, index) => (
                <div key={index} style={styles.card}>
                  <div style={styles.cardHeader}>
                    <IoMedicalOutline style={styles.cardIcon} />
                    <div style={styles.vaccineName}>{item.IMUNOBIOLOGICO || 'Vacina'}</div>
                  </div>

                  <div style={styles.cardContent}>
                    <InfoItem
                      icon={IoCalendarOutline}
                      label="Data"
                      value={formatarData(item.DATA)}
                    />

                    <InfoItem
                      icon={IoFlaskOutline}
                      label="Dose"
                      value={item.DOSE || 'Não informado'}
                    />

                    <InfoItem
                      icon={IoShieldCheckmarkOutline}
                      label="Estratégia"
                      value={item.ESTRATEGIA || 'Não informado'}
                    />

                    <InfoItem
                      icon={IoBarcodeOutline}
                      label="Lote"
                      value={item.LOTE || 'Não informado'}
                    />

                    <InfoItem
                      icon={IoBusiness}
                      label="Fabricante"
                      value={item.FABRICANTE || 'Não informado'}
                    />

                    <InfoItem
                      icon={IoPersonOutline}
                      label="Profissional"
                      value={item.PRO_NOME || 'Não informado'}
                    />
                  </div>
                </div>
              ))
            ) : (
              <div style={styles.emptyContainer}>
                <IoMedicalOutline style={styles.emptyIcon} />
                <div style={styles.emptyText}>
                  {filtro ? 'Nenhuma vacina encontrada para a pesquisa.' : 'Nenhuma vacina encontrada.'}
                </div>
              </div>
            )}
          </>
        )}
      </div>
      {/* <Footer /> */}
    </div>
  );
}